import { ApplicationRef, Injectable, NgZone, OnDestroy, signal } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { EMPTY, Subscription, catchError, exhaustMap, filter, from, race, switchMap, take, timer } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService implements OnDestroy {
  readonly updateAvailable = signal(false);
  private readonly subscriptions = new Subscription();

  constructor(private updates: SwUpdate, app: ApplicationRef, zone: NgZone) {
    if (!updates.isEnabled) {
      return;
    }

    this.subscriptions.add(updates.versionUpdates.subscribe(event => {
      if (event.type === 'VERSION_READY') {
        zone.run(() => this.updateAvailable.set(true));
      } else if (event.type === 'VERSION_INSTALLATION_FAILED') {
        console.error('Application update failed:', event);
      }
    }));

    this.subscriptions.add(updates.unrecoverable.subscribe(event => {
      console.error('Application service worker cannot recover:', event.reason);
    }));

    // Poll outside Angular so recurring timers do not delay app stability or worker registration.
    zone.runOutsideAngular(() => {
      this.subscriptions.add(race(
        app.isStable.pipe(filter(stable => stable), take(1)),
        timer(30000)
      ).pipe(
        take(1),
        switchMap(() => timer(0, 15 * 60 * 1000)),
        filter(() => navigator.onLine),
        exhaustMap(() => from(this.updates.checkForUpdate()).pipe(
          catchError(error => {
            console.error('Failed to check for an application update:', error);
            return EMPTY;
          })
        ))
      ).subscribe());
    });
  }

  refresh(): void {
    // Reload rather than activating in place, which could mix old code with new lazy chunks.
    window.location.reload();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
